import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface AboutHero {
  title?: string;
  subtitle?: string;
  imageUrl?: string;
  features?: string[];
}

export interface AboutValue {
  icon?: string;
  title?: string;
  description?: string;
}

export interface AboutTeamMember {
  name?: string;
  role?: string;
  bio?: string;
  avatar?: string;
  initials?: string;
  /**
   * The `team_members.id` this card was synced from (Team tab → About editor).
   * Lets a re-sync recognise an already-imported member and avoid duplicating
   * them, even after the owner renames the card.
   */
  sourceId?: string;
}

export interface AboutCta {
  title?: string;
  subtitle?: string;
  buttonText?: string;
}

export interface AboutResultImage {
  url?: string;
  alt?: string;
}

export interface AboutResults {
  title?: string;
  subtitle?: string;
  images?: AboutResultImage[];
}

export interface AboutMilestone {
  year?: string;
  event?: string;
}

export interface AboutContent {
  hero?: AboutHero;
  journeyTitle?: string;
  journeyImageUrl?: string;
  milestones?: AboutMilestone[];
  valuesTitle?: string;
  values?: AboutValue[];
  teamTitle?: string;
  team?: AboutTeamMember[];
  results?: AboutResults;
  cta?: AboutCta;
}

/**
 * The tenant's edited About page content (page=`about`, section=`all`), as
 * authored in the visual About admin. Powers the public About page (Roadmap 1H)
 * so it reflects the tenant's real story instead of hardcoded founder copy.
 * Returns null when the tenant has never edited the page.
 */
export function useAboutContent(orgId: string | undefined) {
  return useQuery({
    queryKey: ['public-about-content', orgId],
    enabled: Boolean(orgId),
    queryFn: async (): Promise<AboutContent | null> => {
      const { data, error } = await supabase
        .from('page_content')
        .select('content')
        .eq('organization_id', orgId!)
        .eq('page', 'about')
        .eq('section', 'all')
        .maybeSingle();

      if (error) throw error;

      return (data?.content as AboutContent | undefined) ?? null;
    },
  });
}

/**
 * A row from the Team tab (`team_members`) as far as the About sync cares about
 * it. Photo columns are optional/defensive — the table may not have one today,
 * so we read whichever of the common names is present.
 */
export interface TeamMemberSource {
  id: string;
  name?: string | null;
  email?: string | null;
  role?: string | null;
  title?: string | null;
  avatar_url?: string | null;
  photo_url?: string | null;
  image_url?: string | null;
}

/**
 * The team members managed on Admin > Team. Used to auto-populate the About
 * page team editor so members added there also appear in Company Info and on
 * the public About page, without a second round of manual data entry.
 */
export function useOrgTeamMembers(orgId: string | undefined) {
  return useQuery({
    queryKey: ['about-team-source', orgId],
    enabled: Boolean(orgId),
    queryFn: async (): Promise<TeamMemberSource[]> => {
      const { data, error } = await supabase
        .from('team_members')
        .select('*')
        .eq('organization_id', orgId!)
        .order('name');

      if (error) throw error;

      return (data as TeamMemberSource[] | null) ?? [];
    },
  });
}

// The About page renders at most three team cards, so the editor caps at three.
const MAX_TEAM_CARDS = 3;

const normalize = (value: string | null | undefined) => (value ?? '').trim().toLowerCase();

const initialsOf = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? '')
    .join('');

const sourceAvatar = (member: TeamMemberSource) =>
  member.avatar_url || member.photo_url || member.image_url || '';

// team_members.role is always set; title is a defensive fallback for other shapes.
const sourceRole = (member: TeamMemberSource) => member.role || member.title || 'Team member';

// A card already represents a source row if it was synced from it (sourceId) or
// shares its name (covers cards authored manually before the sync existed).
const cardMatchesSource = (card: AboutTeamMember, member: TeamMemberSource) =>
  (Boolean(card.sourceId) && card.sourceId === member.id) ||
  (Boolean(card.name) && normalize(card.name) === normalize(member.name));

/**
 * Merge Team-tab members into the authored About team so members added on the
 * Team tab automatically appear in the About editor (and the public page).
 *
 * - Manually authored cards are preserved; their bio/avatar/initials/role are
 *   never overwritten — only empty fields are backfilled from the source row.
 * - Source rows already represented (by id or name) are not duplicated.
 * - The total is capped at the three cards the public page can show.
 */
export function mergeTeamMembersIntoAbout(
  about: AboutContent | null,
  sources: TeamMemberSource[],
): AboutContent {
  const base = about ?? {};
  const existing = (base.team ?? []).map((card) => ({ ...card }));

  const claimedSourceIds = new Set<string>();
  for (const card of existing) {
    const match = sources.find((member) => cardMatchesSource(card, member));
    if (!match) continue;
    claimedSourceIds.add(match.id);
    if (!card.sourceId) card.sourceId = match.id;
    if (!card.role) card.role = sourceRole(match);
    if (!card.avatar) {
      const avatar = sourceAvatar(match);
      if (avatar) card.avatar = avatar;
    }
    if (!card.initials && card.name) card.initials = initialsOf(card.name);
  }

  const merged = [...existing];
  for (const member of sources) {
    if (merged.length >= MAX_TEAM_CARDS) break;
    if (claimedSourceIds.has(member.id)) continue;
    if (merged.some((card) => cardMatchesSource(card, member))) continue;
    const name = (member.name ?? '').trim();
    if (!name) continue;
    merged.push({
      name,
      role: sourceRole(member),
      bio: '',
      avatar: sourceAvatar(member),
      initials: initialsOf(name),
      sourceId: member.id,
    });
  }

  // Don't introduce an empty team array where none was authored — leave the row
  // untouched so the public page keeps falling back to its default team.
  if (merged.length === 0 && base.team === undefined) return base;
  return { ...base, team: merged };
}
